/**
 * app-posture-brief.ts — Editorial copy layer for the App Posture page
 * standfirst (the page formerly known as App Permissions).
 *
 * The "score" here is inventory composition, not a 0-100 number — the verdict
 * keys off the count of tier-1 (Critical + High) apps, the unverified-tier-1
 * count, and recent consent volume.
 *
 * Loading state: when `summary` is null/undefined we return a neutral
 * "Reading inventory…" brief.
 */

import type {
  OAuthApp,
  OAuthAppSummary,
} from '@/components/SecurityCenter/appPermissionsTypes'

/* -------------------------------------------------------------------------- */
/* Public shape                                                               */
/* -------------------------------------------------------------------------- */

export interface AppPostureBrief {
  kicker: string
  headlineSubject: string
  headlineModifier: string
  weakLink: string
  deck: string
  verdict: string
  byline: string
  delta: { value: number; days: number }
}

export interface AppPostureTopConcern {
  displayName: string
  isVerifiedPublisher: boolean
  tier: string
}

export interface AppPostureBriefInput {
  userEmail?: string
  tenantId?: string
  summary?: OAuthAppSummary | null
  /** The single highest-AI-score app for the deck. */
  topConcernApp?: AppPostureTopConcern
  /** Apps consented in the last 7 days. Null means we don't have the rollup yet. */
  newThisWeek?: number | null
  /** 30-day delta in tier-1 count (Critical + High). Negative is good (fewer tier-1). */
  tier1Delta30d?: number | null
  /** Optional override — derived from per-app data when unavailable on the summary. */
  unverifiedTier1Count?: number
  asOfDate?: Date
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function formatKicker(now: Date): string {
  const fmt = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long' })
  return `Today's Brief · ${fmt.format(now)}`
}

function localDayKey(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function hash(s: string): number {
  let h = 5381
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i)
  }
  return h >>> 0
}

function pickVariant<T>(variants: readonly T[], seed: string, slot: string): T {
  if (variants.length === 0) throw new Error(`pickVariant: empty variants for slot "${slot}"`)
  const idx = hash(`${seed}|${slot}`) % variants.length
  return variants[idx]
}

/* -------------------------------------------------------------------------- */
/* Verdict variants                                                           */
/* -------------------------------------------------------------------------- */

const VERDICT_HEALTHY: readonly string[] = [
  'Your apps are in good shape.',
  'Inventory is settled.',
  'App posture looks healthy today.',
]

/** Templates take an `{n}` token (word-form: One/Two/Three). */
const VERDICT_HEALTHY_FEW: readonly string[] = [
  'Mostly settled. {n} to review.',
  'Quiet. {n} worth a look.',
  '{n} to review. Otherwise quiet.',
]

const VERDICT_HEALTHY_OVERREACH: readonly string[] = [
  'Mostly quiet. Unverified ones stand out.',
  'Quiet, but unverified apps reach wide.',
  'Settled. Unverified ones need eyes.',
]

const VERDICT_SPRAWLING: readonly string[] = [
  'Tier-one has grown wide.',
  'Crowded tier-one. Trim first.',
  'Tier-one needs a real review.',
]

/** Templates take an `{n}` token (newThisWeek count). */
const VERDICT_VOLATILE: readonly string[] = [
  'Busy week. {n} new arrivals.',
  'Consent picked up. {n} new.',
  'Lively week. {n} new apps.',
]

function numberWord(n: number): string {
  return ['zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six'][n] ?? String(n)
}

function pickVerdict(
  tier1: number,
  unverifiedTier1: number,
  newThisWeek: number | null | undefined,
  seed: string,
): string {
  // Volatile-week verdict overrides when consent activity spiked.
  if (typeof newThisWeek === 'number' && newThisWeek >= 5) {
    const tpl = pickVariant(VERDICT_VOLATILE, seed, 'verdict.volatile')
    return tpl.replace('{n}', String(newThisWeek))
  }

  if (tier1 === 0) return pickVariant(VERDICT_HEALTHY, seed, 'verdict.healthy')

  if (tier1 <= 2) {
    if (unverifiedTier1 > 0) {
      return pickVariant(VERDICT_HEALTHY_OVERREACH, seed, 'verdict.healthy.overreach')
    }
    const tpl = pickVariant(VERDICT_HEALTHY_FEW, seed, 'verdict.healthy.few')
    return tpl.replace('{n}', numberWord(tier1))
  }

  return pickVariant(VERDICT_SPRAWLING, seed, 'verdict.sprawling')
}

/* -------------------------------------------------------------------------- */
/* Modifier + weak-link                                                       */
/* -------------------------------------------------------------------------- */

function pickModifier(
  tier1: number,
  total: number,
  newThisWeek: number | null | undefined,
): string {
  if (typeof newThisWeek === 'number' && newThisWeek >= 5) return 'volatile'
  if (tier1 === 0) return 'healthy'
  if (total > 0 && tier1 / total >= 0.15) return 'sprawling'
  if (tier1 <= 2) return 'steady'
  return 'crowded'
}

const WEAKLINK_CLEAN: readonly string[] = [
  'Nothing needs eyes on it this week.',
  'No tier-one apps to chase.',
  'Inventory is quiet today.',
]

/** Templates take `{n}` — word form. */
const WEAKLINK_FEW_TIER1: readonly string[] = [
  '{n} tier-one apps to review when you can.',
  '{n} apps need a closer look.',
  '{n} hold critical scopes worth a look.',
]

const WEAKLINK_UNVERIFIED: readonly string[] = [
  'The unverified ones are the place to start.',
  'Unverified apps hold tenant scopes. Start there.',
  'A few unverified publishers stand out.',
]

const WEAKLINK_SPRAWL: readonly string[] = [
  'Tier-one apps reach too wide. Trim scope.',
  'Tier-one has grown crowded. Start there.',
  'The tier-one count keeps climbing.',
]

function buildWeakLink(
  tier1: number,
  unverifiedTier1: number,
  seed: string,
): string {
  if (tier1 === 0) return pickVariant(WEAKLINK_CLEAN, seed, 'weakLink.clean')
  if (tier1 > 5) return pickVariant(WEAKLINK_SPRAWL, seed, 'weakLink.sprawl')
  if (unverifiedTier1 > 0) return pickVariant(WEAKLINK_UNVERIFIED, seed, 'weakLink.unverified')
  const tpl = pickVariant(WEAKLINK_FEW_TIER1, seed, 'weakLink.few')
  return tpl.replace('{n}', numberWord(tier1))
}

/* -------------------------------------------------------------------------- */
/* Deck                                                                       */
/* -------------------------------------------------------------------------- */

/** Templates take `{phrase}` — already-bolded app name. */
const RISK_TOP_APP: readonly string[] = [
  '{phrase} stands out. It has the most permission of any app in your tenant.',
  '{phrase} is the one to look at first. It holds the broadest tier-one scopes.',
  '{phrase} reaches further than anything else on the list right now.',
]

const RISK_UNVERIFIED: readonly string[] = [
  'A few **unverified publishers** hold tenant-wide scopes. The unverified ones are the place to start.',
  'The **unverified publishers** with broad scopes are where the biggest move sits today.',
  'Unverified publishers with admin-consent scopes stand out. Start there.',
]

const RISK_HYGIENE: readonly string[] = [
  'No single app dominates the picture. The rest is routine housekeeping.',
  "Nothing dominates the picture. It's housekeeping from here.",
  'The remaining items are housekeeping rather than headline risks.',
]

const RISK_SPRAWL: readonly string[] = [
  'The tier-one population has grown wide. Pruning the broadest scopes lifts posture fastest.',
  'Too many apps hold tier-one scopes in your tenant. The biggest of them is the place to start.',
]

const ACTION_HOLD: readonly string[] = [
  "Keep doing what you're doing. The consent-review cadence is working.",
  'Keep it steady and keep the routine reviews running.',
]

const ACTION_REVIEW: readonly string[] = [
  'Walk the tier-one list when you get a chance this week.',
  'Run the tier-one review this week to keep the floor steady.',
]

const ACTION_PRUNE: readonly string[] = [
  'Plan a real consent-review week, not a routine sweep.',
  'This one needs a tier-one prune, not business-as-usual.',
]

function buildDeck(
  tier1: number,
  unverifiedTier1: number,
  topConcern: AppPostureTopConcern | undefined,
  seed: string,
): string {
  const risk = (() => {
    if (topConcern?.displayName) {
      const phrase = `**${topConcern.displayName}**`
      const tpl = pickVariant(RISK_TOP_APP, seed, 'deck.risk.topApp')
      return tpl.replace('{phrase}', phrase)
    }
    if (unverifiedTier1 > 0) return pickVariant(RISK_UNVERIFIED, seed, 'deck.risk.unverified')
    if (tier1 > 5) return pickVariant(RISK_SPRAWL, seed, 'deck.risk.sprawl')
    return pickVariant(RISK_HYGIENE, seed, 'deck.risk.hygiene')
  })()

  const action = (() => {
    if (tier1 === 0) return pickVariant(ACTION_HOLD, seed, 'deck.action.hold')
    if (tier1 <= 2) return pickVariant(ACTION_REVIEW, seed, 'deck.action.review')
    return pickVariant(ACTION_PRUNE, seed, 'deck.action.prune')
  })()

  return `${risk} ${action}`
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

export function composeAppPostureBrief(
  input: AppPostureBriefInput = {},
): AppPostureBrief {
  const userEmail = input.userEmail ?? 'you'
  const now = input.asOfDate ?? new Date()
  const delta = typeof input.tier1Delta30d === 'number' ? input.tier1Delta30d : 0

  const byline =
    userEmail === 'you'
      ? 'Brief by **Guardian Continuous Assessment**'
      : `Brief by **Guardian Continuous Assessment** · For **${userEmail}**`

  if (input.summary == null) {
    return {
      kicker: formatKicker(now),
      headlineSubject: 'App posture is',
      headlineModifier: 'loading',
      weakLink: 'Reading the latest signal.',
      deck: 'Pulling the latest Microsoft 365 OAuth inventory. The brief updates once the sync resolves.',
      verdict: 'Reading inventory…',
      byline,
      delta: { value: delta, days: 30 },
    }
  }

  const summary = input.summary
  const tier1 = (summary.criticalCount ?? 0) + (summary.highCount ?? 0)
  const unverifiedTier1 = input.unverifiedTier1Count ?? 0
  const total = summary.totalApps ?? 0
  const seed = `${input.tenantId ?? 'guardian'}:${localDayKey(now)}`

  return {
    kicker: formatKicker(now),
    headlineSubject: 'App posture is',
    headlineModifier: pickModifier(tier1, total, input.newThisWeek),
    weakLink: buildWeakLink(tier1, unverifiedTier1, seed),
    deck: buildDeck(tier1, unverifiedTier1, input.topConcernApp, seed),
    verdict: pickVerdict(tier1, unverifiedTier1, input.newThisWeek, seed),
    byline,
    delta: { value: delta, days: 30 },
  }
}

/* -------------------------------------------------------------------------- */
/* Why-it-matters fallback                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Compose the "why-it-matters" paragraph for an app card on the App Posture
 * triptych (§8.2). Returns the AI-generated `aiRiskSummary` verbatim when
 * present so the operator gets the model's reasoning. When the AI hasn't run
 * yet, falls back to a deterministic template stitched from the real per-app
 * counts — `criticalPermissionCount`, `highPermissionCount`,
 * `daysSinceLastSignIn`. Every clause is conditional on a real field being
 * present; we never fabricate prose for an empty app.
 *
 * Markdown emphasis (`**...**`) is preserved so the consumer can render it
 * via the same `<strong>`-substitution pattern PillarBrief uses.
 */
export function whyItMatters(
  app: Pick<
    OAuthApp,
    | 'aiRiskSummary'
    | 'criticalPermissionCount'
    | 'highPermissionCount'
    | 'daysSinceLastSignIn'
  >,
): string {
  if (app.aiRiskSummary && app.aiRiskSummary.trim().length > 0) {
    return app.aiRiskSummary
  }

  const parts: string[] = []

  if (app.criticalPermissionCount > 0) {
    const plural = app.criticalPermissionCount !== 1 ? 's' : ''
    parts.push(
      `Holds **${app.criticalPermissionCount} critical** permission${plural}`,
    )
  }

  if (app.highPermissionCount > 0) {
    const plural = app.highPermissionCount !== 1 ? 's' : ''
    parts.push(
      `${app.highPermissionCount} high-risk permission${plural}`,
    )
  }

  const days = app.daysSinceLastSignIn
  if (days != null && days > 30) {
    parts.push(`last used ${days} days ago, possibly stale`)
  } else if (days == null) {
    parts.push('no sign-in record')
  }

  if (parts.length === 0) {
    // No critical / high counts and a recent sign-in — there's not much to
    // say without inventing a verdict. Surface a neutral, honest sentence.
    return 'No critical or high-risk scopes flagged. Review for least-privilege fit.'
  }

  return parts.join('. ') + '.'
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

export const __TEST_ONLY__ = {
  VERDICT_HEALTHY,
  VERDICT_HEALTHY_FEW,
  VERDICT_HEALTHY_OVERREACH,
  VERDICT_SPRAWLING,
  VERDICT_VOLATILE,
  WEAKLINK_CLEAN,
  WEAKLINK_FEW_TIER1,
  WEAKLINK_UNVERIFIED,
  WEAKLINK_SPRAWL,
  RISK_TOP_APP,
  RISK_UNVERIFIED,
  RISK_HYGIENE,
  RISK_SPRAWL,
}
